if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getCartUi() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const customOptions =
          this.closest('product-info')?.querySelector('mn-product-custom-options') ||
          document.querySelector('mn-product-custom-options');
        const wallpaperOrder =
          this.closest('product-info')?.querySelector('mn-wallpaper-order') ||
          document.querySelector('mn-wallpaper-order');

        customOptions?.syncFromSelection?.();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const formData = new FormData(this.form);
        this.appendSampleWallpaperDetails(formData);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        const finishLoading = () => {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        };

        let customPayload = null;
        if (this.isSamplePurchase()) {
          const samplePropsError = this.appendCustomOptionsForSample(formData, customOptions);
          if (samplePropsError) {
            this.handleErrorMessage();
            this.dispatchCartErrorEvent(samplePropsError, 'INVALID');
            linesUpdateDeferred?.reject(new Error(samplePropsError));
            finishLoading();
            return;
          }
        } else if (customOptions?.buildCartAddPayload) {
          if (typeof customOptions.ensureColorFeeVariants === 'function') {
            await customOptions.ensureColorFeeVariants();
          }
          customPayload = customOptions.buildCartAddPayload(formData);
          if (customPayload?.error) {
            this.handleErrorMessage();
            this.dispatchCartErrorEvent(customPayload.error, 'INVALID');
            linesUpdateDeferred?.reject(new Error(customPayload.error));
            finishLoading();
            return;
          }
        }

        const isWallpaper =
          this.dataset.mnIsWallpaper === 'true' ||
          wallpaperOrder?.dataset?.mnIsWallpaper === 'true';

        // Sample: no wall width/height — only optional custom color/name on the sample SKU.
        if (
          !this.isSamplePurchase() &&
          isWallpaper &&
          wallpaperOrder?.measureRoot &&
          !wallpaperOrder.measureComplete
        ) {
          this.handleErrorMessage();
          this.dispatchCartErrorEvent('Enter wall width and height', 'INVALID');
          linesUpdateDeferred?.reject(new Error('Enter wall width and height'));
          finishLoading();
          return;
        }

        const submitWithPayload = (payload) => {
          const cart = this.getCartUi();
          this.cart = cart;

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          const sectionIds = cart ? cart.getSectionsToRender().map((section) => section.id) : [];

          // Wallpaper / multi-line custom options: always JSON so underscore
          // fee properties and fee SKUs are preserved (FormData can drop them).
          let sellableItems = Array.isArray(payload?.items) ? [...payload.items] : null;
          let feeBatchedInRequest = false;
          if (payload?.feeItem?.id) {
            if (!sellableItems) sellableItems = [];
            const already = sellableItems.some(
              (item) =>
                Number(item.id) === Number(payload.feeItem.id) &&
                item.properties?._fee_type === 'design_packaging'
            );
            if (!already) {
              const feeItem = { ...payload.feeItem };
              if (!feeItem.parent_id) {
                const main = sellableItems.find((item) => !item?.properties?._fee_type);
                const parentId = Number(main?.id) || Number(variantId) || 0;
                if (parentId > 0) feeItem.parent_id = parentId;
              }
              sellableItems.push(feeItem);
            }
            feeBatchedInRequest = true;
          }
          const useJson =
            (sellableItems && sellableItems.length > 1) ||
            (sellableItems &&
              sellableItems.some(
                (item) =>
                  item?.properties &&
                  Object.keys(item.properties).some((key) => String(key).startsWith('_'))
              ));

          // If we had to fall back to FormData (single line, no JSON), fee was not sent yet.
          if (!useJson) feeBatchedInRequest = false;

          if (useJson && sellableItems?.length) {
            config.headers['Content-Type'] = 'application/json';
            const body = { items: sellableItems };
            if (sectionIds.length) {
              body.sections = sectionIds;
              body.sections_url = window.location.pathname;
              cart.setActiveElement?.(document.activeElement);
            }
            config.body = JSON.stringify(body);
          } else {
            delete config.headers['Content-Type'];
            const mainItem = sellableItems?.[0];
            const props = mainItem?.properties;
            if (props) {
              Object.entries(props).forEach(([key, value]) => {
                if (value == null || value === '') return;
                formData.set(`properties[${key}]`, String(value));
              });
            }
            if (mainItem?.id) formData.set('id', String(mainItem.id));
            if (mainItem?.quantity != null) formData.set('quantity', String(mainItem.quantity));
            if (sectionIds.length) {
              formData.set('sections', sectionIds.join(','));
              formData.set('sections_url', window.location.pathname);
              cart.setActiveElement?.(document.activeElement);
            }
            config.body = formData;
          }

          const normalizeAddResponse = (response) => {
            if (!response || response.status) return response;
            const lines = Array.isArray(response.items) ? response.items : response.key ? [response] : [];
            const primary =
              lines.find((line) => line?.properties?._fee_type !== 'design_packaging') || lines[0];
            if (primary) {
              if (response.id == null) response.id = primary.id;
              if (response.key == null) response.key = primary.key;
            }
            return response;
          };

          const fetchCartSections = async (ids) => {
            if (!ids?.length) return {};
            const root = window.Shopify?.routes?.root || '/';
            try {
              const sections = await fetch(`${root}?sections=${ids.join(',')}`).then((r) => r.json());
              return sections && typeof sections === 'object' ? sections : {};
            } catch (e) {
              console.error(e);
              return {};
            }
          };

          const openCartUi = async (response) => {
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            let state = normalizeAddResponse(response) || {};
            const freshSections = await fetchCartSections(sectionIds);
            state = {
              ...state,
              sections: { ...(state.sections || {}), ...freshSections },
            };

            if (!state.key) {
              try {
                const cartJs = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
                const primary =
                  (cartJs?.items || []).find((line) => line?.properties?._fee_type !== 'design_packaging') ||
                  cartJs?.items?.[0];
                if (primary?.key) state.key = primary.key;
                if (primary?.id) state.id = primary.id;
              } catch (e) {
                console.error(e);
              }
            }

            try {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
            } catch (e) {
              console.error(e);
            }

            try {
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!this.error) {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: variantId,
                  cartData: state,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              }
            } catch (e) {
              console.error(e);
            }

            this.error = false;

            const paint = () => {
              try {
                cart.renderContents(state);
              } catch (e) {
                console.error(e);
                cart.open?.(document.activeElement);
              }

              // Guarantee the notification/drawer opens even if section HTML was incomplete.
              requestAnimationFrame(() => {
                const notification = document.getElementById('cart-notification');
                const drawer = document.querySelector('cart-drawer');
                if (notification && !notification.classList.contains('active')) {
                  cart.open?.(document.activeElement);
                  notification.classList.add('animate', 'active');
                } else if (drawer && !drawer.classList.contains('active')) {
                  drawer.open?.(document.activeElement);
                }
              });
            };

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => setTimeout(paint), { once: true });
              quickAddModal.hide(true);
            } else {
              paint();
            }
          };

          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then(async (response) => {
              response = normalizeAddResponse(response);

              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: variantId,
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description || response.message);
                this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
                linesUpdateDeferred?.reject(new Error(response.description || response.message));

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              }

              const feeItem = payload?.feeItem;
              if (feeItem?.id && !feeBatchedInRequest) {
                try {
                  const fee = { ...feeItem };
                  if (!fee.parent_id) {
                    const parentId = Number(variantId) || 0;
                    if (parentId > 0) fee.parent_id = parentId;
                  }
                  await fetch(`${routes.cart_add_url}`, {
                    ...fetchConfig('javascript'),
                    headers: {
                      ...fetchConfig('javascript').headers,
                      'X-Requested-With': 'XMLHttpRequest',
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ items: [fee] }),
                  }).then((r) => r.json());
                } catch (e) {
                  console.error(e);
                }
              }

              await openCartUi(response);
            })
            .catch((e) => {
              console.error(e);
              this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
              linesUpdateDeferred?.reject(e);
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (cart?.classList?.contains('is-empty')) cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner')?.classList.add('hidden');
              CartPerformance.measureFromEvent('add:user-action', evt);
            });
        };

        if (!this.isSamplePurchase() && isWallpaper) {
          this.mergeWallpaperServiceFee(customPayload, formData, wallpaperOrder)
            .then((payload) => {
              if (payload?.error) {
                this.handleErrorMessage();
                this.dispatchCartErrorEvent(payload.error, 'INVALID');
                linesUpdateDeferred?.reject(new Error(payload.error));
                finishLoading();
                return;
              }
              submitWithPayload(payload);
            })
            .catch((e) => {
              console.error(e);
              this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
              linesUpdateDeferred?.reject(e);
              finishLoading();
            });
          return;
        }

        submitWithPayload(customPayload);
      }

      /**
       * Wallpaper ATC: rolls line + $150 service fee once per order (skip if fee already in cart).
       * Fee is returned as feeItem and added in a second request so cart UI always opens.
       */
      async mergeWallpaperServiceFee(customPayload, formData, wallpaperOrder) {
        const variantId = Number(formData.get('id'));
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        let items = customPayload?.items ? [...customPayload.items] : null;
        if (!items?.length) {
          const properties = {};
          for (const [key, value] of formData.entries()) {
            const match = String(key).match(/^properties\[(.+)\]$/);
            if (match && value && !match[1].startsWith('_')) properties[match[1]] = value;
          }
          items = [{ id: variantId, quantity, properties }];
        }

        items = items.filter((item) => {
          const t = item?.properties?._fee_type;
          // Keep custom color/name fee SKUs; design fee is added separately as feeItem.
          return t !== 'design_packaging';
        });

        const main = items.find((item) => !item?.properties?._fee_type) || items[0] || {
          id: variantId,
          quantity,
          properties: {},
        };
        main.properties = { ...(main.properties || {}) };
        const feeCents = Number(wallpaperOrder?.getDesignFeeCents?.()) || 20000;
        main.properties['_design_fee_cents'] = String(feeCents);
        // Only store a fee variant id when a separate fee SKU should be charged
        // (skip when roll prices already include the service fee).
        if (!wallpaperOrder?.pricesIncludeFee?.()) {
          const designFeeVariantId =
            Number(wallpaperOrder?.config?.designFeeVariantId) ||
            Number(wallpaperOrder?.dataset?.designFeeVariantId) ||
            0;
          if (designFeeVariantId > 0) {
            main.properties['_design_fee_variant_id'] = String(designFeeVariantId);
          }
        }
        if (wallpaperOrder?.matchedHeightLabel) {
          main.properties['Roll height'] = wallpaperOrder.matchedHeightLabel;
        }
        if (wallpaperOrder?.enteredWidth != null) {
          main.properties['Wall width (cm)'] = String(wallpaperOrder.enteredWidth);
        }
        if (wallpaperOrder?.enteredHeight != null) {
          main.properties['Wall height (cm)'] = String(wallpaperOrder.enteredHeight);
        }
        main.properties['Rolls'] = quantity === 1 ? '1 roll' : `${quantity} rolls`;
        delete main.properties['Size'];
        delete main.properties['Custom order service'];

        const mainIndex = items.indexOf(main);
        if (mainIndex >= 0) items[mainIndex] = main;
        else items.unshift(main);

        let cart = null;
        try {
          cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        } catch (e) {
          cart = null;
        }

        const feeAlreadyInCart = Boolean(
          cart?.items?.some((line) => line?.properties?._fee_type === 'design_packaging')
        );

        let feeItem = null;
        if (!feeAlreadyInCart) {
          feeItem = wallpaperOrder?.buildDesignFeeItem?.(variantId) || null;
        }

        return { items, feeItem };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const wallpaperOrder =
          this.closest('product-info')?.querySelector('mn-wallpaper-order[data-mn-is-wallpaper="true"]') ||
          document.querySelector('mn-wallpaper-order[data-mn-is-wallpaper="true"]');

        // Full wallpaper order owns ATC enable/disable from wall size.
        // Sample purchase never requires width/height.
        if (!this.isSamplePurchase() && wallpaperOrder?.measureRoot) {
          const soldOutVisible = Boolean(this.querySelector('.sold-out-message:not([hidden])'));
          if (!wallpaperOrder.measureComplete) {
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButton.setAttribute('data-mn-measure-disabled', 'true');
            return;
          }
          // Measure complete: ignore transient disables from variant refresh (no sold-out text).
          if (disable && !text && !soldOutVisible) {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.removeAttribute('data-mn-measure-disabled');
            return;
          }
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          // Don't overwrite wallpaper ATC money label when measure owns the full-order button.
          if (
            this.isSamplePurchase() ||
            !(wallpaperOrder?.measureRoot && wallpaperOrder.measureComplete)
          ) {
            this.submitButtonText.textContent = this.getAvailableSubmitLabel();
          }
        }
      }

      getAvailableSubmitLabel() {
        return window.variantStrings.addToCart;
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      isSamplePurchase() {
        if (this.dataset.samplePurchase === 'true') return true;
        const idInput = this.variantIdInput;
        return Boolean(
          idInput?.dataset?.mnSamplePurchase === 'true' ||
            idInput?.hasAttribute('data-sample-variant-id') ||
            this.form?.querySelector('[data-mn-sample-purchase="true"], [name="properties[_purchase_type]"][value="sample"]')
        );
      }

      /**
       * Copy custom color/name onto the sample line for cart display.
       * Sample never requires wall width/height.
       * Colors: use custom picks if selected; otherwise wall default odd/even colors.
       * Does not add fee catalog lines (sample price stays flat).
       * @returns {string|null} validation error message, if any
       */
      appendCustomOptionsForSample(formData, customOptions) {
        if (!customOptions || !formData) return null;

        customOptions.refreshElements?.();
        customOptions.syncFromSelection?.();

        const nameChecked = Boolean(customOptions.config?.allowCustomName && customOptions.nameToggle?.checked);
        const colorActive = Boolean(customOptions.isCustomColorActive?.());
        const wallPreview = Boolean(
          customOptions.config?.wallPreview || customOptions.dataset?.wallPreview === 'true'
        );
        const selections = customOptions.getColorSelections?.() || [];

        // Name only required when the name toggle is on.
        if (nameChecked && !customOptions.nameInput?.value?.trim()) {
          customOptions.showError?.('Please enter a custom name.', 'name');
          return 'Please enter a custom name.';
        }

        // Custom color toggle on with no picks: require picks unless wall defaults exist.
        if (colorActive && !selections.length && !wallPreview) {
          customOptions.showError?.('Please pick a custom color.', 'color');
          return 'Please pick a custom color.';
        }

        if (nameChecked && customOptions.nameInput?.value?.trim()) {
          formData.set('properties[Custom name]', customOptions.nameInput.value.trim());
        }

        // Prefer explicit custom selections; otherwise attach wall default / preview colors.
        if (colorActive && selections.length) {
          const colorValue = customOptions.getSelectedColorPropertyValue?.();
          const colorData = customOptions.getSelectedColorPropertyValueStructured?.();
          if (colorValue) formData.set('properties[Custom color]', String(colorValue));
          if (colorData) formData.set('properties[_custom_color_data]', String(colorData));
          return null;
        }

        const wall =
          typeof customOptions.getWallPreviewColors === 'function'
            ? customOptions.getWallPreviewColors()
            : typeof customOptions.getWallDefaultColors === 'function'
              ? { ...customOptions.getWallDefaultColors(), active: false }
              : null;

        const c1 = customOptions.normalizeHex?.(wall?.c1) || wall?.c1 || '';
        const c2 = customOptions.normalizeHex?.(wall?.c2) || wall?.c2 || '';
        if (c1 || c2) {
          const parts = [];
          const structured = [];
          if (c1) {
            parts.push(`Stripe odd [${c1}]`);
            structured.push(`Stripe odd|${c1}||`);
          }
          if (c2) {
            parts.push(`Stripe even [${c2}]`);
            structured.push(`Stripe even|${c2}||`);
          }
          formData.set('properties[Custom color]', parts.join(', '));
          formData.set('properties[_custom_color_data]', structured.join('@@'));
        }

        return null;
      }

      appendSampleWallpaperDetails(formData) {
        if (!this.isSamplePurchase() || !formData) return;

        // Dropdown pickers already submit options[Name]; copy those into line properties.
        const optionFromSelects = [];
        for (const [key, value] of formData.entries()) {
          const match = String(key).match(/^options\[(.+)\]$/);
          if (match?.[1] && value != null && String(value).trim() !== '') {
            optionFromSelects.push([match[1], String(value).trim()]);
          }
        }
        optionFromSelects.forEach(([name, value]) => {
          formData.set(`properties[${name}]`, value);
        });

        const root = this.closest('product-info') || this.closest('.product') || document;
        const variantSelects = root.querySelector('variant-selects');
        if (!variantSelects) return;

        const optionNames = [];
        variantSelects
          .querySelectorAll('fieldset[data-option-name], .product-form__input[data-option-name]')
          .forEach((el) => {
            const name = el.getAttribute('data-option-name')?.trim();
            if (name && !optionNames.includes(name)) optionNames.push(name);
          });

        let optionValues = [];
        const selectedVariantEl = variantSelects.querySelector('[data-selected-variant]');
        if (selectedVariantEl?.textContent) {
          try {
            const variant = JSON.parse(selectedVariantEl.textContent);
            if (Array.isArray(variant?.options)) {
              optionValues = variant.options;
            } else {
              optionValues = [variant?.option1, variant?.option2, variant?.option3].filter(
                (value) => value != null && value !== ''
              );
            }
          } catch (e) {
            optionValues = [];
          }
        }

        const checkedRadios = [...variantSelects.querySelectorAll('input[type="radio"]:checked')];
        const selects = [...variantSelects.querySelectorAll('select')];

        optionNames.forEach((name, index) => {
          if (formData.get(`properties[${name}]`)) return;

          let value = optionValues[index];

          if (value == null || value === '') {
            const checked = checkedRadios.find(
              (input) =>
                input.getAttribute('data-option-name') === name ||
                (input.name && input.name.startsWith(`${name}-`))
            );
            if (checked?.value) value = checked.value;
          }

          if (value == null || value === '') {
            const select = selects.find((el) => el.getAttribute('name') === `options[${name}]`);
            if (select?.value) value = select.value;
          }

          if (name && value != null && String(value).trim() !== '') {
            formData.set(`properties[${name}]`, String(value).trim());
          }
        });
      }
    }
  );
}
